import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, Pencil, Trash2, Save, Loader2, Film, Scissors } from 'lucide-react';

// Base element interface - compatible with TTMLElement
interface TimelineElement {
  id: string;
  start: number;
  end: number;
  type: string;
  content: string;
  title?: string;
}

interface ClipEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  element: TimelineElement | null;
  elementType: 'clip' | 'edit';
  onSave: (element: TimelineElement, updates: ClipEditUpdates) => Promise<void>;
  onDelete: (element: TimelineElement) => Promise<void>;
  onSeek: (time: number) => void;
  isDarkMode: boolean;
  canEdit: boolean;
}

export interface ClipEditUpdates {
  title: string;
  clipType?: string;        // For clips: viral, educational, humorous, etc.
  description?: string;
  priority?: string;        // For edits: high, medium, low
  suggestion?: string;
  improvement?: string;
  visual?: string;
  start: number;
  end: number;
}

// Helper to format time
const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  if (hours > 0) {
    return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Parse time string (MM:SS or HH:MM:SS) to seconds
const parseTimeToSeconds = (timeStr: string): number | null => {
  const parts = timeStr.split(':').map(p => parseInt(p.trim(), 10));
  if (parts.some(p => isNaN(p))) return null;

  if (parts.length === 2) {
    // MM:SS
    return parts[0] * 60 + parts[1];
  } else if (parts.length === 3) {
    // HH:MM:SS
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  return null;
};

// Parse element content to extract fields
const parseElementContent = (content: string, elementType: 'clip' | 'edit'): Partial<ClipEditUpdates> => {
  const lines = content.split('<br/>').map(l => l.trim()).filter(Boolean);
  const result: Partial<ClipEditUpdates> = {};

  if (lines.length === 0) return result;

  // First line format: [CLIP] Title | Type | Description | Visual
  // or: [EDIT] Title | Suggestion | Improvement | Priority
  const firstLine = lines[0];
  const prefix = elementType === 'clip' ? '[CLIP]' : '[EDIT]';

  if (firstLine.startsWith(prefix)) {
    const parts = firstLine.substring(prefix.length).split('|').map(p => p.trim());

    if (elementType === 'clip') {
      result.title = parts[0] || '';
      result.clipType = parts[1] || '';
      result.description = parts[2] || '';
      result.visual = parts[3] || '';
    } else {
      result.title = parts[0] || '';
      result.suggestion = parts[1] || '';
      result.improvement = parts[2] || '';
      result.priority = parts[3] || '';
    }
  }

  return result;
};

function ClipEditModal({
  isOpen,
  onClose,
  element,
  elementType,
  onSave,
  onDelete,
  onSeek,
  isDarkMode,
  canEdit
}: ClipEditModalProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Edit form state
  const [editTitle, setEditTitle] = useState('');
  const [editClipType, setEditClipType] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editPriority, setEditPriority] = useState('');
  const [editSuggestion, setEditSuggestion] = useState('');
  const [editImprovement, setEditImprovement] = useState('');
  const [editVisual, setEditVisual] = useState('');
  const [editStartTime, setEditStartTime] = useState('');
  const [editEndTime, setEditEndTime] = useState('');

  // Reset form when element changes or modal opens
  useEffect(() => {
    if (element) {
      const parsed = parseElementContent(element.content, elementType);
      setEditTitle(parsed.title || element.title || '');
      setEditClipType(parsed.clipType || '');
      setEditDescription(parsed.description || '');
      setEditPriority(parsed.priority || '');
      setEditSuggestion(parsed.suggestion || '');
      setEditImprovement(parsed.improvement || '');
      setEditVisual(parsed.visual || '');
      setEditStartTime(formatTime(element.start));
      setEditEndTime(formatTime(element.end));
    }
    setIsEditing(false);
    setShowDeleteConfirm(false);
    setError(null);
  }, [element, isOpen, elementType]);

  const handleStartEdit = () => {
    setIsEditing(true);
    setError(null);
  };

  const handleCancelEdit = () => {
    // Reset to original values
    if (element) {
      const parsed = parseElementContent(element.content, elementType);
      setEditTitle(parsed.title || element.title || '');
      setEditClipType(parsed.clipType || '');
      setEditDescription(parsed.description || '');
      setEditPriority(parsed.priority || '');
      setEditSuggestion(parsed.suggestion || '');
      setEditImprovement(parsed.improvement || '');
      setEditVisual(parsed.visual || '');
      setEditStartTime(formatTime(element.start));
      setEditEndTime(formatTime(element.end));
    }
    setIsEditing(false);
    setError(null);
  };

  const handleSave = async () => {
    if (!element) return;

    if (!editTitle.trim()) {
      setError('Title is required');
      return;
    }

    // Parse and validate times
    const newStart = parseTimeToSeconds(editStartTime);
    const newEnd = parseTimeToSeconds(editEndTime);

    if (newStart === null) {
      setError('Invalid start time format (use MM:SS or HH:MM:SS)');
      return;
    }
    if (newEnd === null) {
      setError('Invalid end time format (use MM:SS or HH:MM:SS)');
      return;
    }
    if (newEnd <= newStart) {
      setError('End time must be after start time');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const updates: ClipEditUpdates = {
        title: editTitle.trim(),
        start: newStart,
        end: newEnd
      };

      if (elementType === 'clip') {
        updates.clipType = editClipType.trim() || undefined;
        updates.description = editDescription.trim() || undefined;
        updates.visual = editVisual.trim() || undefined;
      } else {
        updates.priority = editPriority.trim() || undefined;
        updates.suggestion = editSuggestion.trim() || undefined;
        updates.improvement = editImprovement.trim() || undefined;
      }

      await onSave(element, updates);
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!element) return;

    setIsDeleting(true);
    setError(null);

    try {
      await onDelete(element);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to delete ${elementType}`);
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (!isSaving && !isDeleting) {
      onClose();
    }
  };

  if (!element) return null;

  const parsed = parseElementContent(element.content, elementType);
  const displayTitle = parsed.title || element.title || `Untitled ${elementType}`;
  const Icon = elementType === 'clip' ? Film : Scissors;
  const accentColor = elementType === 'clip' ? 'rgb(59, 130, 246)' : 'rgb(239, 68, 68)'; // blue-500 or red-500

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-2xl max-w-2xl w-full mx-4 overflow-hidden`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`flex items-center justify-between px-5 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center gap-3">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: accentColor }}
                >
                  <Icon size={16} className="text-white" />
                </div>
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {isEditing ? `Edit ${elementType === 'clip' ? 'Clip' : 'Edit Point'}` : (elementType === 'clip' ? 'Clip' : 'Edit Point')}
                </h3>
              </div>
              <div className="flex items-center gap-2">
                {canEdit && !isEditing && !showDeleteConfirm && (
                  <>
                    <button
                      onClick={handleStartEdit}
                      className={`p-2 rounded-full transition-colors ${
                        isDarkMode
                          ? 'hover:bg-gray-700 text-gray-400 hover:text-blue-400'
                          : 'hover:bg-gray-100 text-gray-500 hover:text-blue-600'
                      }`}
                      title={`Edit ${elementType}`}
                    >
                      <Pencil size={18} />
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className={`p-2 rounded-full transition-colors ${
                        isDarkMode
                          ? 'hover:bg-gray-700 text-gray-400 hover:text-red-400'
                          : 'hover:bg-gray-100 text-gray-500 hover:text-red-600'
                      }`}
                      title={`Delete ${elementType}`}
                    >
                      <Trash2 size={18} />
                    </button>
                  </>
                )}
                <button
                  onClick={handleClose}
                  disabled={isSaving || isDeleting}
                  className={`p-2 rounded-full transition-colors ${
                    isDarkMode
                      ? 'hover:bg-gray-700 text-gray-400'
                      : 'hover:bg-gray-100 text-gray-500'
                  }`}
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Error message */}
            {error && (
              <div className="mx-5 mt-4 px-3 py-2 rounded-lg bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 text-sm">
                {error}
              </div>
            )}

            {/* Delete Confirmation */}
            {showDeleteConfirm && (
              <div className="px-5 py-4">
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-red-900/30 border border-red-700' : 'bg-red-50 border border-red-200'}`}>
                  <p className={`text-sm mb-4 ${isDarkMode ? 'text-red-200' : 'text-red-800'}`}>
                    Are you sure you want to delete this {elementType}? This action cannot be undone.
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={isDeleting}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        isDarkMode
                          ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className="px-4 py-2 rounded-lg font-medium text-white bg-red-600 hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                      {isDeleting ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <Trash2 size={16} />
                          Delete {elementType === 'clip' ? 'Clip' : 'Edit Point'}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Content - View Mode */}
            {!isEditing && !showDeleteConfirm && (
              <div className="px-5 py-4 space-y-4">
                {/* Title */}
                <div>
                  <h4 className={`text-xl font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {displayTitle}
                  </h4>
                </div>

                {/* Type/Priority badge */}
                {(elementType === 'clip' ? parsed.clipType : parsed.priority) && (
                  <div className="flex items-center gap-2">
                    <span
                      className="text-sm px-2 py-0.5 rounded-full text-white"
                      style={{ backgroundColor: accentColor }}
                    >
                      {elementType === 'clip' ? parsed.clipType : parsed.priority}
                    </span>
                  </div>
                )}

                {/* Description / Suggestion */}
                {(elementType === 'clip' ? parsed.description : parsed.suggestion) && (
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {elementType === 'clip' ? parsed.description : parsed.suggestion}
                  </p>
                )}

                {/* Improvement (for edits) */}
                {elementType === 'edit' && parsed.improvement && (
                  <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <span className="font-medium">Improvement:</span> {parsed.improvement}
                  </div>
                )}

                {/* Visual (for clips) */}
                {elementType === 'clip' && parsed.visual && (
                  <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <span className="font-medium">Visual:</span> {parsed.visual}
                  </div>
                )}

                {/* Time range - clickable to seek */}
                <button
                  onClick={() => {
                    onSeek(element.start);
                    onClose();
                  }}
                  className={`text-xs flex items-center gap-1.5 px-2 py-1 rounded transition-colors ${
                    isDarkMode
                      ? 'text-blue-400 hover:bg-blue-900/30'
                      : 'text-blue-600 hover:bg-blue-50'
                  }`}
                >
                  <Clock size={12} />
                  {formatTime(element.start)} - {formatTime(element.end)}
                </button>
              </div>
            )}

            {/* Content - Edit Mode */}
            {isEditing && !showDeleteConfirm && (
              <div className="px-5 py-4 space-y-4">
                {/* Title field - required */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    placeholder={`${elementType === 'clip' ? 'Clip' : 'Edit point'} title...`}
                    disabled={isSaving}
                    className={`w-full px-3 py-2 rounded-lg border transition-colors focus:ring-2 focus:border-transparent ${
                      isDarkMode
                        ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                    }`}
                    style={{ '--tw-ring-color': accentColor } as React.CSSProperties}
                  />
                </div>

                {/* Time range row */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Start Time
                    </label>
                    <input
                      type="text"
                      value={editStartTime}
                      onChange={(e) => setEditStartTime(e.target.value)}
                      placeholder="MM:SS"
                      disabled={isSaving}
                      className={`w-full px-3 py-2 rounded-lg border transition-colors focus:ring-2 focus:border-transparent font-mono ${
                        isDarkMode
                          ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                      }`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      End Time
                    </label>
                    <input
                      type="text"
                      value={editEndTime}
                      onChange={(e) => setEditEndTime(e.target.value)}
                      placeholder="MM:SS"
                      disabled={isSaving}
                      className={`w-full px-3 py-2 rounded-lg border transition-colors focus:ring-2 focus:border-transparent font-mono ${
                        isDarkMode
                          ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                      }`}
                    />
                  </div>
                </div>

                {/* Clip-specific fields */}
                {elementType === 'clip' && (
                  <>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Type
                      </label>
                      <select
                        value={editClipType}
                        onChange={(e) => setEditClipType(e.target.value)}
                        disabled={isSaving}
                        className={`w-full px-3 py-2 rounded-lg border transition-colors focus:ring-2 focus:border-transparent ${
                          isDarkMode
                            ? 'bg-gray-900 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      >
                        <option value="">Select type...</option>
                        <option value="Viral Hook">Viral Hook</option>
                        <option value="Educational Nugget">Educational Nugget</option>
                        <option value="Humorous Aside">Humorous Aside</option>
                        <option value="Key Insight">Key Insight</option>
                        <option value="Quotable Moment">Quotable Moment</option>
                        <option value="Technical Deep Dive">Technical Deep Dive</option>
                      </select>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Description
                      </label>
                      <textarea
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        placeholder="Describe this clip..."
                        rows={2}
                        disabled={isSaving}
                        className={`w-full px-3 py-2 rounded-lg border transition-colors focus:ring-2 focus:border-transparent resize-y ${
                          isDarkMode
                            ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-400'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                        }`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Visual Suggestion
                      </label>
                      <input
                        type="text"
                        value={editVisual}
                        onChange={(e) => setEditVisual(e.target.value)}
                        placeholder="Suggested visual treatment..."
                        disabled={isSaving}
                        className={`w-full px-3 py-2 rounded-lg border transition-colors focus:ring-2 focus:border-transparent ${
                          isDarkMode
                            ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-400'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                        }`}
                      />
                    </div>
                  </>
                )}

                {/* Edit-specific fields */}
                {elementType === 'edit' && (
                  <>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Priority
                      </label>
                      <select
                        value={editPriority}
                        onChange={(e) => setEditPriority(e.target.value)}
                        disabled={isSaving}
                        className={`w-full px-3 py-2 rounded-lg border transition-colors focus:ring-2 focus:border-transparent ${
                          isDarkMode
                            ? 'bg-gray-900 border-gray-600 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      >
                        <option value="">Select priority...</option>
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                      </select>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Suggestion
                      </label>
                      <textarea
                        value={editSuggestion}
                        onChange={(e) => setEditSuggestion(e.target.value)}
                        placeholder="What should be edited..."
                        rows={2}
                        disabled={isSaving}
                        className={`w-full px-3 py-2 rounded-lg border transition-colors focus:ring-2 focus:border-transparent resize-y ${
                          isDarkMode
                            ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-400'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                        }`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Improvement
                      </label>
                      <input
                        type="text"
                        value={editImprovement}
                        onChange={(e) => setEditImprovement(e.target.value)}
                        placeholder="Expected improvement..."
                        disabled={isSaving}
                        className={`w-full px-3 py-2 rounded-lg border transition-colors focus:ring-2 focus:border-transparent ${
                          isDarkMode
                            ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-400'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                        }`}
                      />
                    </div>
                  </>
                )}

                {/* Edit mode buttons */}
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      isDarkMode
                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving || !editTitle.trim()}
                    className="px-4 py-2 rounded-lg font-medium text-white transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: accentColor }}
                  >
                    {isSaving ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default ClipEditModal;
